/**
 * Program 12.1 UX3 — Tender intake panel for workspace.
 */
import React, { useMemo } from 'react';
import { Alert, Button, Card, Col, Descriptions, List, Row, Space, Tag, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import { epcInvitationKind, epcInvitationKindLabel } from '@/constants/epcInvitationKind';
import { formatDate } from '@/utils/format';
import {
  buildTenderIntakeReadiness,
  daysUntilDue,
} from './tenderIntakeReadiness';

const { Text, Title, Paragraph } = Typography;

export type EpcTenderIntakePanelProps = {
  tender: Record<string, unknown>;
  documentCount?: number;
  addendaCount?: number;
  prequalGateOk?: boolean;
};

function parseCtx(raw: unknown): Record<string, unknown> {
  if (!raw || typeof raw !== 'object') return {};
  return raw as Record<string, unknown>;
}

function parseMeta(raw: unknown): Record<string, unknown> {
  return parseCtx(raw);
}

export const EpcTenderIntakePanel: React.FC<EpcTenderIntakePanelProps> = ({
  tender,
  documentCount = 0,
  addendaCount = 0,
  prequalGateOk = true,
}) => {
  const navigate = useNavigate();
  const ctx = parseCtx(tender.epc_context_json || tender.epcContextJson);
  const meta = parseMeta(tender.meta_json || tender.metaJson);
  const intake = parseMeta(meta.intake || ctx.intake);

  const readiness = useMemo(
    () =>
      buildTenderIntakeReadiness(tender, {
        documentCount,
        addendaCount,
        prequalGateOk,
      }),
    [tender, documentCount, addendaCount, prequalGateOk]
  );

  const dueRaw = tender.submission_due_at || tender.submissionDueAt;
  const bidRaw = tender.bid_due_at || tender.bidDueAt;
  const daysLeft = daysUntilDue(dueRaw);

  const keyDates = [
    { label: 'Received', value: tender.created_at ? formatDate(String(tender.created_at)) : '—' },
    { label: 'Bid due', value: bidRaw ? formatDate(String(bidRaw)) : '—' },
    { label: 'Submission due', value: dueRaw ? formatDate(String(dueRaw)) : '—' },
    {
      label: 'Clarification deadline',
      value: intake.clarification_deadline
        ? formatDate(String(intake.clarification_deadline))
        : ctx.clarification_deadline
          ? formatDate(String(ctx.clarification_deadline))
          : '—',
    },
  ];

  const commercialParticulars =
    (ctx.commercial_particulars as string | undefined) ||
    (intake.commercial_particulars as string | undefined) ||
    '';

  const submissionMethod =
    (ctx.submission_method as string | undefined) ||
    (intake.submission_method as string | undefined) ||
    '';

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card>
        <Title level={4} style={{ marginTop: 0 }}>
          {String(tender.title || tender.tender_number || 'Tender')}
        </Title>
        <Text type="secondary">{String(tender.client_name || tender.clientName || '—')}</Text>
        <Row gutter={[16, 8]} style={{ marginTop: 12 }}>
          <Col span={8}>
            <Text type="secondary">Received</Text>
            <div>{tender.created_at ? formatDate(String(tender.created_at)) : '—'}</div>
          </Col>
          <Col span={8}>
            <Text type="secondary">Submission due</Text>
            <div>{dueRaw ? formatDate(String(dueRaw)) : '—'}</div>
          </Col>
          <Col span={8}>
            <Text type="secondary">Time remaining</Text>
            <div>{daysLeft != null ? `${daysLeft} days` : '—'}</div>
          </Col>
        </Row>
        <Space wrap style={{ marginTop: 12 }}>
          <Tag color="geekblue">
            {epcInvitationKindLabel(tender.invitation_kind || tender.invitationKind || 'RFT')}
          </Tag>
          <Tag color={readiness.intakeStatus === 'COMPLETE' ? 'green' : 'blue'}>
            INTAKE: {readiness.intakeStatus.replace('_', ' ')}
          </Tag>
          {addendaCount > 0 && (
            <Tag color="orange">{addendaCount} addendum(addenda)</Tag>
          )}
          <Tag>{documentCount} document(s)</Tag>
        </Space>
        {epcInvitationKind(tender.invitation_kind || tender.invitationKind) === 'RFQ' ? (
          <Alert
            style={{ marginTop: 12 }}
            type="info"
            showIcon
            message="Request for Quotation"
            description="This is the client invitation we respond to, not a supplier enquiry. Price it in Estimating. Supplier quotations stay in Procurement."
          />
        ) : (
          <Alert
            style={{ marginTop: 12 }}
            type="info"
            showIcon
            message="Request for Tender"
            description="This is the client invitation we respond to, not a supplier enquiry."
          />
        )}
        {tender.timezone ? (
          <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
            Closing timezone: {String(tender.timezone)}
          </Text>
        ) : null}
      </Card>

      <Card title="Key dates" size="small">
        <Descriptions size="small" column={2} bordered>
          {keyDates.map((d) => (
            <Descriptions.Item key={d.label} label={d.label}>
              {d.value}
            </Descriptions.Item>
          ))}
        </Descriptions>
      </Card>

      <Card title="Commercial particulars" size="small">
        {commercialParticulars ? (
          <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{commercialParticulars}</Paragraph>
        ) : (
          <Text type="secondary">
            No commercial particulars in epc_context_json — capture during intake review.
          </Text>
        )}
      </Card>

      {submissionMethod && (
        <Card title="Submission instructions" size="small">
          <Paragraph style={{ margin: 0 }}>{submissionMethod}</Paragraph>
        </Card>
      )}

      <Card title="Intake readiness" size="small">
        <List
          size="small"
          dataSource={readiness.checks}
          renderItem={(c) => (
            <List.Item>
              <Space>
                <Text>{c.ok ? '✓' : '✕'}</Text>
                <Text>{c.label}</Text>
                {c.detail && <Text type="secondary">{c.detail}</Text>}
              </Space>
            </List.Item>
          )}
        />
        {readiness.ready ? (
          <Button
            type="primary"
            style={{ marginTop: 12 }}
            onClick={() => navigate(`/epc/commercial/tenders/${String(tender.id || tender.tender_id || '')}/bid`)}
          >
            Open Bid Control Room
          </Button>
        ) : (
          <Text type="secondary" style={{ display: 'block', marginTop: 12 }}>
            {readiness.blockerCount} blocker(s) — complete intake before Bid / No-Bid review.
          </Text>
        )}
      </Card>
    </Space>
  );
};

export default EpcTenderIntakePanel;
